import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  InputSearch,
  TableData,
  Pagination,
  UniversalFilter,
  ResetFiltersButton,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import {
  useListQuery,
  useNavigateFromList,
  usePageClamp,
} from '@/hooks/useUrlListState';
import { useTableColumns } from '@/hooks/useTableColumns';
import dayjs from 'dayjs';
import {
  Column,
  Handover,
  HandoverStatus,
  BUSINESS_UNIT_OPTIONS,
  getBusinessUnitLabel,
} from '@/types/common';
import { extractApiErrorMessage } from '@/utils';
import { showErrorToast } from '@/components/toast/globalToast';
import ReturnHandoverModal from './ReturnHandoverModal';
import {
  HANDOVER_FILTER_KEYS,
  HANDOVER_LIST_DEFAULTS,
} from './handoverListQuery';

interface PreReturnHandoverTabProps {
  onEditHandover: (handover: Handover) => void;
  refreshTrigger: number;
  vehicles: any[];
  clients: any[];
  pitstops: any[];
  hubLocations: any[];
  oems: any[];
  models: any[];
  onTotalChange?: (total: number) => void; // New prop to communicate total back to parent
  onDownloadParamsChange?: (params: Record<string, any>) => void;
}

const PreReturnHandoverTab: React.FC<PreReturnHandoverTabProps> = ({
  onEditHandover,
  refreshTrigger,
  vehicles,
  clients,
  pitstops,
  hubLocations,
  oems,
  models,
  onTotalChange,
  onDownloadParamsChange,
}) => {
  const {
    searchTerm,
    searchInput,
    setSearchInput,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
    activeFilters,
    setSearchTerm,
    setCurrentPage,
    setFilters,
    setSort,
    setPageSize,
    hasFilters,
    resetFilters,
    clampToTotal,
  } = useListQuery(HANDOVER_LIST_DEFAULTS, HANDOVER_FILTER_KEYS);
  const [handoverData, setHandoverData] = useState<Handover[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState<Handover | null>(
    null
  );
  const navigate = useNavigateFromList();

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Models',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: any) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'clientIDs',
        title: 'Client',
        icon: 'ri-user-line',
        multiple: true,
        dataSource: clients,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'hubIDs',
        title: 'Pitstop',
        icon: 'ri-store-2-line',
        multiple: true,
        dataSource: pitstops,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'vehicleHubIDs',
        title: 'Hub Location',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: hubLocations,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        staticOptions: BUSINESS_UNIT_OPTIONS,
      },
    ],
    [oems, models, clients, pitstops, hubLocations]
  );

  const fetchHandovers = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: Record<string, any>,
      size?: number
    ) => {
      setIsLoading(true);
      try {
        const params: any = {
          page: page.toString(),
          size: (size || pageSize).toString(),
          statuses: HandoverStatus.ACTIVE, // Only active handovers
          isPreReturn: true, // Assuming this is the correct boolean flag
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        // Add filters
        if (filters?.oemIDs && filters.oemIDs.length > 0) {
          params.oemIDs = filters.oemIDs.join(',');
        }
        if (filters?.modelIDs && filters.modelIDs.length > 0) {
          params.modelIDs = filters.modelIDs.join(',');
        }
        if (filters?.clientIDs && filters.clientIDs.length > 0) {
          params.clientIDs = filters.clientIDs.join(',');
        }
        if (filters?.hubIDs && filters.hubIDs.length > 0) {
          params.hubIDs = filters.hubIDs.join(',');
        }
        if (filters?.vehicleHubIDs && filters.vehicleHubIDs.length > 0) {
          params.vehicleHubIDs = filters.vehicleHubIDs.join(',');
        }
        if (filters?.businessUnits && filters.businessUnits.length > 0) {
          params.businessUnits = filters.businessUnits.join(',');
        }

        // Still mock-backed. To restore the real call, re-add
        // `import { getListHandover } from '@/services/api/handovers';`:
        // const response = await getListHandover(params);

        // Mock data logic for pure slicing
        const dummyData: any[] = Array.from({ length: size || pageSize }).map(
          (_, index) => ({
            ID: `dummy-${page}-${index}`,
            platNomor: 'B 1234 ABC',
            oemName:
              index < 4 ? 'Electrum' : index < 7 ? 'Smoot' : 'Maka Motors',
            modelName: index < 4 ? 'H3' : index < 7 ? 'Tempur' : 'Cavalry',
            driverName: 'Naipin',
            driverCode: 'MTR0000123',
            vehicleHubName: 'Hub Jaksel',
            vehicleBusinessUnit: 'QUICK_COMMERCE',
            clientName: 'Sayurbox',
            hubName: 'Sayurbox-Fatmawati',
            resignType: 'Self-Resign',
            requestDate: '2025-06-01T00:00:00Z',
          })
        );

        await new Promise((resolve) => setTimeout(resolve, 500));

        setHandoverData(dummyData);
        const totalCount = 160; // Mock total count for pagination (e.g. 16 pages)
        setTotal(totalCount);
        onTotalChange?.(totalCount);

        setCurrentPage(page);
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load pre-return handovers'
        );
        showErrorToast('Error', errorMessage);
        setHandoverData([]);
        setTotal(0);
        onTotalChange?.(0); // Notify parent that total is 0
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize, onTotalChange, setCurrentPage]
  );

  useEffect(() => {
    fetchHandovers(currentPage, searchTerm, activeFilters, pageSize);
  }, [
    fetchHandovers,
    currentPage,
    searchTerm,
    activeFilters,
    refreshTrigger,
    pageSize,
  ]);

  // A page restored from the URL can sit past the end once the list shrinks.
  usePageClamp(clampToTotal, total, isLoading);

  useEffect(() => {
    if (!onDownloadParamsChange) return;

    const params: Record<string, any> = {
      statuses: HandoverStatus.ACTIVE,
      isPreReturn: true,
    };

    if (activeFilters?.oemIDs && activeFilters.oemIDs.length > 0) {
      params.oemIDs = activeFilters.oemIDs.join(',');
    }
    if (activeFilters?.modelIDs && activeFilters.modelIDs.length > 0) {
      params.modelIDs = activeFilters.modelIDs.join(',');
    }
    if (activeFilters?.clientIDs && activeFilters.clientIDs.length > 0) {
      params.clientIDs = activeFilters.clientIDs.join(',');
    }
    if (activeFilters?.hubIDs && activeFilters.hubIDs.length > 0) {
      params.hubIDs = activeFilters.hubIDs.join(',');
    }
    if (
      activeFilters?.vehicleHubIDs &&
      activeFilters.vehicleHubIDs.length > 0
    ) {
      params.vehicleHubIDs = activeFilters.vehicleHubIDs.join(',');
    }
    if (
      activeFilters?.businessUnits &&
      activeFilters.businessUnits.length > 0
    ) {
      params.businessUnits = activeFilters.businessUnits.join(',');
    }

    onDownloadParamsChange(params);
  }, [
    onDownloadParamsChange,
    currentPage,
    pageSize,
    searchTerm,
    activeFilters,
  ]);

  // Memoized handlers to prevent function recreation on every render
  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const handleEditClick = useCallback(
    (record: Handover) => {
      onEditHandover(record);
      setActionDropdownOpen(null);
    },
    [onEditHandover]
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActionDropdownOpen(null);
      }
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeyPress);
    }

    // Always cleanup on unmount
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [actionDropdownOpen]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // NOTE: this is the only thing that opens ReturnHandoverModal (rendered
  // below), but no control calls it yet — the trigger was never wired into the
  // row actions. Kept rather than deleted so the return flow isn't lost; wire
  // it up (or remove the modal too) when the flow is finished.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleReturnHandover = (handover: Handover) => {
    // REPOSSESSION tidak menahan: tujuan case penarikan justru unitnya
    // kembali, jadi return yang berhasil sekaligus menutup case-nya di sisi
    // backend. Proses lain tetap menahan — di sana unitnya sedang dipakai
    // untuk sesuatu yang belum selesai. Case yang menunggu persetujuan
    // settlement juga masih ditolak, tapi itu keputusan backend: hanya di
    // sana status case-nya diketahui.
    const isVehicleUnavailable =
      handover.vehicleProcessType &&
      handover.vehicleProcessType !== 'AVAILABLE' &&
      handover.vehicleProcessType !== 'REPOSSESSION';

    if (isVehicleUnavailable) {
      showErrorToast(
        'Vehicle Not Available',
        `This vehicle is currently in ${handover.vehicleProcessType} process. The vehicle must be in AVAILABLE status before returning the handover.`
      );
      setActionDropdownOpen(null);
      return;
    }

    setSelectedHandover(handover);
    setIsReturnModalOpen(true);
    setActionDropdownOpen(null);
  };

  const handleReturnSuccess = () => {
    // Refresh the handover list after successful return
    fetchHandovers(currentPage, searchTerm, activeFilters, pageSize);
  };

  // Helper function to create action dropdown actions for handovers
  const createHandoverActions = useCallback(
    (record: Handover): ActionDropdownAction[] => [
      {
        key: 'detail',
        label: 'Detail',
        icon: 'ri-eye-line',
        onClick: () => {
          setActionDropdownOpen(null);
          navigate(`/handover/pre-return/${record.ID}`);
        },
        separateAfter: true,
      },
      {
        key: 'edit',
        label: 'Edit',
        icon: 'ri-pencil-line',
        onClick: () => handleEditClick(record),
      },
    ],
    [handleEditClick, navigate]
  );

  // Client-side sorting
  const sortedHandoverData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return handoverData;
    }

    return [...handoverData].sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [handoverData, sortField, sortDirection]);

  const columns: Column[] = [
    {
      key: 'platNomor',
      title: 'Vehicle',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div>
          <span className="text-[14px] font-medium">{value}</span>
          <br />
          <span className="text-[12px] text-gray-500">
            {record.oemName + ' - ' + record.modelName}
          </span>
        </div>
      ),
    },

    {
      key: 'driverName',
      title: 'Mitra Name',
      width: 'w-32',
      render: (value: string, record: Handover) => (
        <div
          className={`text-[14px] ${
            record.driverID
              ? 'text-underline text-blue-600 cursor-pointer hover:text-blue-800'
              : 'text-gray-900 cursor-default'
          }`}
          onClick={(e) => {
            if (record.driverID) {
              e.stopPropagation();
              const backOfficeUrl =
                process.env.REACT_APP_BACK_OFFICE_URL ||
                'https://back-office.dashelectric.co';
              window.open(
                `${backOfficeUrl}/driver/${record.driverID}`,
                '_blank'
              );
            }
          }}
        >
          {value ? (
            <>
              <span className="font-medium">{value}</span>
              <br />
              <span className="text-[12px] text-gray-500">
                {record.driverCode}
              </span>
            </>
          ) : (
            <div>-</div>
          )}
        </div>
      ),
    },
    {
      key: 'vehicleHubName',
      title: 'Hub Location',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'vehicleBusinessUnit',
      title: 'Business Unit',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px] font-normal">
          {value !== '-' ? (
            getBusinessUnitLabel(value)
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </span>
      ),
    },
    {
      key: 'clientName',
      title: 'Client',
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    {
      key: 'hubName',
      title: 'Pitstop',
      width: 'w-40',
      render: (value: string) => (
        <span className="text-[14px] font-medium">{value}</span>
      ),
    },
    {
      key: 'resignType',
      title: 'Resign Type',
      sortable: true,
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'requestDate',
      title: 'Request Date',
      sortable: true,
      width: 'w-32',
      render: (value: string) => (
        <span className="text-[14px]">
          {value ? dayjs(value).format('DD/MM/YYYY') : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      sticky: 'right',
      render: (value: any, record: Handover, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={createHandoverActions(record)}
            ariaLabel={`Actions for handover ${record.platNomor}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Handover>({
    storageKey: 'handovers-pre-return',
    columns,
  });

  return (
    <>
      {/* Controls */}
      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-2">
          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              debounceMs={300}
              onClear={() => handleSearch('')}
              size="small"
            />
          </div>

          <UniversalFilter
            filters={filterConfigs}
            currentFilters={activeFilters}
            onApply={(filters: Record<string, any>) => {
              // Close any open action dropdowns
              setActionDropdownOpen(null);
              setFilters(filters);
            }}
            onClear={() => {
              // Close any open action dropdowns
              setActionDropdownOpen(null);
              setFilters({});
            }}
          />
          <ResetFiltersButton
            visible={hasFilters()}
            onReset={() => resetFilters()}
          />

          <ColumnSettings {...columnSettingsProps} />
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <TableData
          data={sortedHandoverData}
          columns={visibleColumns}
          loading={isLoading}
          sorting={{
            field: sortField,
            direction: sortDirection,
            onSort: setSort,
          }}
          onRowClick={(record) => navigate(`/handover/pre-return/${record.ID}`)}
          size="small"
          className="min-w-full"
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          fetchHandovers(1, searchTerm, activeFilters, newPageSize);
        }}
      />

      {/* Return Handover Modal */}
      <ReturnHandoverModal
        isOpen={isReturnModalOpen}
        handover={selectedHandover}
        allowedReturnReasons={[
          'CONTRACT_END',
          'MITRA_RESIGN',
          'CLIENT_REQUEST',
          'DUTY_COMPLETED',
          'REPOSSESSION',
          'OTHER',
        ]}
        onClose={() => {
          setIsReturnModalOpen(false);
          setSelectedHandover(null);
        }}
        onSuccess={handleReturnSuccess}
      />
    </>
  );
};

export default PreReturnHandoverTab;
